import { styled } from "@material-ui/core/styles";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PropTypes from "prop-types";
import * as React from "react";
import { usd } from "../../../util/formatCurrency";
import { buildCustomizations } from "./customizations";

const priceLabel = (cents) => (cents === 0 ? "FREE" : usd(cents));

export default function CustomizationsSummary({
  addons = [],
  services = [],
  cartProductId,
}) {
  const { sections, count, totalCents } = React.useMemo(
    () => buildCustomizations({ addons, services }),
    [addons, services],
  );

  const [expanded, setExpanded] = React.useState(false);

  if (count === 0) {
    return null;
  }

  const detailsId = `customizations-${cartProductId}`;
  const showSectionLabels = sections.length > 1;

  return (
    <StyledRoot>
      <StyledSummaryButton
        type="button"
        aria-controls={detailsId}
        aria-expanded={expanded}
        onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
      >
        {expanded ? (
          <ExpandMoreIcon fontSize="small" />
        ) : (
          <ChevronRightIcon fontSize="small" />
        )}
        <span>Customizations ({count})</span>
        <StyledTotal>{usd(totalCents)}</StyledTotal>
      </StyledSummaryButton>

      <StyledDetails id={detailsId} hidden={!expanded}>
        {sections.map((section) => (
          <StyledSection key={section.id}>
            {showSectionLabels && (
              <StyledSectionLabel>{section.label}</StyledSectionLabel>
            )}

            <StyledList>
              {section.items.map((item) => (
                <StyledItem key={item.key}>
                  <StyledItemRow>
                    <span>
                      {item.quantity > 1 && (
                        <StyledQuantity>{item.quantity} &times;</StyledQuantity>
                      )}
                      {item.label}
                    </span>
                    <StyledItemPrice>{priceLabel(item.cents)}</StyledItemPrice>
                  </StyledItemRow>

                  {item.texts.map((text, index) => (
                    <StyledItemText
                      key={`${item.key}-text-${index}`}
                      title={text}
                    >
                      {text}
                    </StyledItemText>
                  ))}
                </StyledItem>
              ))}
            </StyledList>
          </StyledSection>
        ))}
      </StyledDetails>
    </StyledRoot>
  );
}

CustomizationsSummary.propTypes = {
  addons: PropTypes.array,
  cartProductId: PropTypes.number.isRequired,
  services: PropTypes.array,
};

const StyledRoot = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

const StyledSummaryButton = styled("button")(({ theme }) => ({
  ...theme.typography.caption,
  display: "grid",
  gridTemplateColumns: "auto 1fr auto",
  alignItems: "center",
  gap: theme.spacing(0.5),
  width: "100%",
  minHeight: 44,
  margin: 0,
  padding: theme.spacing(0, 0.5),
  background: "none",
  border: "none",
  borderRadius: theme.shape.borderRadius,
  textAlign: "left",
  fontWeight: 500,
  color: theme.palette.text.secondary,
  cursor: "pointer",
  "&:hover": {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.text.primary}`,
    outlineOffset: 2,
  },
}));

const StyledTotal = styled("span")({
  fontVariantNumeric: "tabular-nums",
});

const StyledDetails = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 0.5, 0.5, 3),
  "&[hidden]": {
    display: "none",
  },
}));

const StyledSection = styled("section")({});

const StyledSectionLabel = styled("h5")(({ theme }) => ({
  ...theme.typography.overline,
  margin: 0,
  lineHeight: 1.6,
  color: theme.palette.text.secondary,
}));

const StyledList = styled("ul")({
  listStyle: "none",
  margin: 0,
  padding: 0,
});

const StyledItem = styled("li")(({ theme }) => ({
  ...theme.typography.caption,
  padding: theme.spacing(0.25, 0),
  color: theme.palette.text.primary,
}));

const StyledItemRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: theme.spacing(1),
}));

const StyledQuantity = styled("span")(({ theme }) => ({
  fontWeight: 600,
  marginInlineEnd: theme.spacing(0.5),
  fontVariantNumeric: "tabular-nums",
}));

const StyledItemPrice = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
}));

/* Quotes and dedications run long; two lines is enough to recognise one, and
 * the title attribute carries the rest. */
const StyledItemText = styled("p")(({ theme }) => ({
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  overflowWrap: "anywhere",
  margin: theme.spacing(0.25, 0, 0),
  color: theme.palette.text.secondary,
  fontStyle: "italic",
  lineHeight: 1.4,
}));
